import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Table, Card, Button, Modal, Form, Input, InputNumber, Select,
  DatePicker, Space, Tag, Typography, Statistic, Row, Col, message,
  Popconfirm, Tooltip, Upload, Drawer, Descriptions, Divider, Badge,
  Alert, Progress, Spin
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined,
  FileTextOutlined, DownloadOutlined, CalendarOutlined,
  DollarOutlined, WarningOutlined, FilePdfOutlined,
  FileWordOutlined, FileUnknownOutlined, RobotOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { contractService, clientVendorService } from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const Contracts = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [contracts, setContracts] = useState([]);
  const [stats, setStats] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [form] = Form.useForm();

  // AI Extraction state
  const [extracting, setExtracting] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [vendorMatches, setVendorMatches] = useState([]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [contractsData, statsData, vendorsData] = await Promise.all([
        contractService.getAll(),
        contractService.getStats(),
        clientVendorService.getAll(),
      ]);
      setContracts(contractsData);
      setStats(statsData);
      setVendors(vendorsData);
    } catch (error) {
      message.error('Failed to load contracts');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-open detail drawer when navigated with ?id= param
  useEffect(() => {
    const contractId = searchParams.get('id');
    if (contractId && contracts.length > 0) {
      const contract = contracts.find(c => c.id === parseInt(contractId, 10));
      if (contract) {
        setSelectedContract(contract);
        setDrawerVisible(true);
        // Clear the param so closing the drawer doesn't re-open it
        setSearchParams({}, { replace: true });
      }
    }
  }, [contracts, searchParams, setSearchParams]);

  const showAddModal = () => {
    setEditingContract(null);
    setSelectedFile(null);
    setExtractedData(null);
    setVendorMatches([]);
    form.resetFields();
    setIsModalVisible(true);
  };

  const showEditModal = (record) => {
    setEditingContract(record);
    setSelectedFile(null);
    setExtractedData(null);
    setVendorMatches([]);
    form.setFieldsValue({
      ...record,
      start_date: record.start_date ? dayjs(record.start_date) : null,
      end_date: record.end_date ? dayjs(record.end_date) : null,
    });
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingContract(null);
    setSelectedFile(null);
    setExtractedData(null);
    setVendorMatches([]);
    form.resetFields();
  };

  const handleFileSelect = async (file) => {
    setSelectedFile(file);

    // Only extract for new contracts, not when editing
    if (editingContract) {
      return false;
    }

    // Start AI extraction
    setExtracting(true);
    setExtractedData(null);
    setVendorMatches([]);

    try {
      const result = await contractService.extractMetadata(file);
      setExtractedData(result);
      setVendorMatches(result.vendor_matches || []);

      // Auto-populate form with extracted data
      const formValues = {};

      if (result.title) formValues.title = result.title;
      if (result.contract_type) formValues.contract_type = result.contract_type;
      if (result.contract_number) formValues.contract_number = result.contract_number;
      if (result.total_value) formValues.total_value = result.total_value;
      if (result.currency) formValues.currency = result.currency;
      if (result.auto_renew !== null) formValues.auto_renew = result.auto_renew;
      if (result.renewal_notice_days) formValues.renewal_notice_days = result.renewal_notice_days;
      if (result.renewal_term_months) formValues.renewal_term_months = result.renewal_term_months;

      // Parse dates
      if (result.start_date) {
        try {
          formValues.start_date = dayjs(result.start_date);
        } catch (e) {
          console.error('Failed to parse start date:', result.start_date);
        }
      }
      if (result.end_date) {
        try {
          formValues.end_date = dayjs(result.end_date);
        } catch (e) {
          console.error('Failed to parse end date:', result.end_date);
        }
      }

      // If we have vendor matches with high confidence, select the best one
      if (result.vendor_matches && result.vendor_matches.length > 0) {
        const bestMatch = result.vendor_matches[0];
        if (bestMatch.similarity_score >= 85) {
          formValues.client_vendor_id = bestMatch.client_vendor_id;
        }
      }

      // Add payment terms to notes if extracted
      if (result.payment_terms) {
        formValues.notes = `Payment Terms: ${result.payment_terms}`;
      }

      form.setFieldsValue(formValues);
      message.success('Contract metadata extracted successfully!');

    } catch (error) {
      console.error('Extraction failed:', error);
      message.warning('Could not extract metadata. Please fill in the fields manually.');
    } finally {
      setExtracting(false);
    }

    return false; // Prevent default upload behavior
  };

  const handleSubmit = async (values) => {
    try {
      const data = {
        ...values,
        start_date: values.start_date?.toISOString(),
        end_date: values.end_date?.toISOString(),
      };

      if (editingContract) {
        await contractService.update(editingContract.id, data);
        if (selectedFile) {
          await contractService.uploadFile(editingContract.id, selectedFile);
        }
        message.success('Contract updated');
      } else {
        await contractService.create(data, selectedFile);
        message.success('Contract created');
      }

      setIsModalVisible(false);
      form.resetFields();
      setSelectedFile(null);
      fetchData();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    try {
      await contractService.delete(id);
      message.success('Contract deleted');
      fetchData();
    } catch (error) {
      message.error('Failed to delete contract');
    }
  };

  const handleDownload = (contract) => {
    const url = contractService.getDownloadUrl(contract.id);
    window.open(url, '_blank');
  };

  const handleContractClick = (record) => {
    setSelectedContract(record);
    setDrawerVisible(true);
  };

  const getContractStatus = (contract) => {
    if (!contract.end_date) return null;
    const daysUntil = dayjs(contract.end_date).diff(dayjs(), 'day');
    if (daysUntil < 0) return { text: 'Expired', color: 'red', days: daysUntil };
    if (daysUntil <= 30) return { text: 'Expiring Soon', color: 'red', days: daysUntil };
    if (daysUntil <= 90) return { text: 'Expiring', color: 'orange', days: daysUntil };
    return { text: 'Active', color: 'green', days: daysUntil };
  };

  const getFileIcon = (fileType) => {
    if (fileType === 'pdf') return <FilePdfOutlined style={{ color: '#ff4d4f' }} />;
    if (['doc', 'docx'].includes(fileType)) return <FileWordOutlined style={{ color: '#1890ff' }} />;
    return <FileUnknownOutlined />;
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'green',
      draft: 'default',
      expired: 'red',
      terminated: 'volcano',
    };
    return colors[status] || 'default';
  };

  const getTypeColor = (type) => {
    const colors = {
      msa: 'blue',
      sow: 'purple',
      nda: 'cyan',
      amendment: 'orange',
      renewal: 'green',
      other: 'default',
    };
    return colors[type] || 'default';
  };

  const columns = [
    {
      title: 'Contract',
      dataIndex: 'title',
      key: 'title',
      render: (title, record) => (
        <Space>
          {record.file_type && getFileIcon(record.file_type)}
          <span style={{ fontWeight: 500 }}>{title}</span>
        </Space>
      ),
    },
    {
      title: 'Vendor',
      key: 'vendor',
      render: (_, record) => {
        const vendor = vendors.find(v => v.id === record.client_vendor_id);
        return vendor?.vendor?.name || '-';
      },
    },
    {
      title: 'Type',
      dataIndex: 'contract_type',
      key: 'contract_type',
      render: (type) => type && (
        <Tag color={getTypeColor(type)}>
          {type.toUpperCase()}
        </Tag>
      ),
      filters: [
        { text: 'MSA', value: 'msa' },
        { text: 'SOW', value: 'sow' },
        { text: 'NDA', value: 'nda' },
        { text: 'Amendment', value: 'amendment' },
        { text: 'Renewal', value: 'renewal' },
      ],
      onFilter: (value, record) => record.contract_type === value,
    },
    {
      title: 'Value',
      dataIndex: 'total_value',
      key: 'total_value',
      render: (value, record) => value ? (
        <span>${value.toLocaleString()} {record.currency}</span>
      ) : '-',
      sorter: (a, b) => (a.total_value || 0) - (b.total_value || 0),
    },
    {
      title: 'End Date',
      dataIndex: 'end_date',
      key: 'end_date',
      render: (date, record) => {
        if (!date) return '-';
        const status = getContractStatus(record);
        return (
          <Space>
            {dayjs(date).format('MMM D, YYYY')}
            {status && status.color !== 'green' && (
              <Tooltip title={`${Math.abs(status.days)} days ${status.days < 0 ? 'ago' : 'remaining'}`}>
                <Badge status={status.color === 'red' ? 'error' : 'warning'} />
              </Tooltip>
            )}
          </Space>
        );
      },
      sorter: (a, b) => {
        if (!a.end_date) return 1;
        if (!b.end_date) return -1;
        return dayjs(a.end_date).unix() - dayjs(b.end_date).unix();
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {record.file_path && (
            <Tooltip title="Download">
              <Button
                type="text"
                icon={<DownloadOutlined />}
                onClick={(e) => { e.stopPropagation(); handleDownload(record); }}
              />
            </Tooltip>
          )}
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={(e) => { e.stopPropagation(); showEditModal(record); }}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this contract?"
            onConfirm={(e) => { e?.stopPropagation(); handleDelete(record.id); }}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => e.stopPropagation()}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={2}>Contract Repository</Title>
      <Text type="secondary">
        Manage and track all vendor contracts in one place
      </Text>

      {/* Stats Cards */}
      {stats && (
        <Row gutter={16} style={{ marginTop: 24, marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total Contracts"
                value={stats.total_contracts}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Active Contracts"
                value={stats.active_contracts}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total Value"
                value={stats.total_value}
                prefix={<DollarOutlined />}
                precision={0}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card
              hoverable={stats.expiring_soon > 0}
              onClick={() => stats.expiring_soon > 0 && message.info('Filter by expiring contracts coming soon')}
            >
              <Statistic
                title="Expiring Soon"
                value={stats.expiring_soon}
                prefix={<WarningOutlined />}
                valueStyle={{ color: stats.expiring_soon > 0 ? '#cf1322' : '#8c8c8c' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Contracts Table */}
      <Card
        title="All Contracts"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={showAddModal}>
            Add Contract
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={contracts}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          onRow={(record) => ({
            onClick: () => handleContractClick(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingContract ? 'Edit Contract' : 'Add Contract'}
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ status: 'active', currency: 'USD' }}
        >
          {/* File Upload with AI Extraction - at the top for new contracts */}
          {!editingContract && (
            <Form.Item label="Contract Document">
              <Upload
                beforeUpload={handleFileSelect}
                onRemove={() => {
                  setSelectedFile(null);
                  setExtractedData(null);
                  setVendorMatches([]);
                }}
                fileList={selectedFile ? [selectedFile] : []}
                accept=".pdf,.doc,.docx,.txt"
                disabled={extracting}
              >
                <Button icon={<UploadOutlined />} loading={extracting}>
                  {extracting ? 'Analyzing Document...' : 'Upload Document (AI will extract details)'}
                </Button>
              </Upload>
              {extracting && (
                <div style={{ marginTop: 12 }}>
                  <Spin size="small" style={{ marginRight: 8 }} />
                  <Text type="secondary">
                    <RobotOutlined style={{ marginRight: 4 }} />
                    AI is reading your contract and extracting key information...
                  </Text>
                </div>
              )}
            </Form.Item>
          )}

          {/* Extraction Success Banner */}
          {extractedData && !extracting && (
            <Alert
              message={
                <Space>
                  <CheckCircleOutlined />
                  <span>AI extracted contract details</span>
                  <Tag color="blue">
                    {Math.round(extractedData.extraction_confidence * 100)}% confidence
                  </Tag>
                </Space>
              }
              description={
                <div>
                  {extractedData.vendor_name && (
                    <div>
                      <Text type="secondary">Detected vendor: </Text>
                      <Text strong>{extractedData.vendor_name}</Text>
                    </div>
                  )}
                  {extractedData.key_terms && extractedData.key_terms.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <Text type="secondary">Key terms found: </Text>
                      {extractedData.key_terms.slice(0, 3).map((term, idx) => (
                        <Tag key={idx} style={{ marginTop: 4 }}>
                          {term.term}: {term.value?.substring(0, 30)}{term.value?.length > 30 ? '...' : ''}
                        </Tag>
                      ))}
                      {extractedData.key_terms.length > 3 && (
                        <Tag>+{extractedData.key_terms.length - 3} more</Tag>
                      )}
                    </div>
                  )}
                </div>
              }
              type="success"
              style={{ marginBottom: 16 }}
              closable
            />
          )}

          <Form.Item
            name="title"
            label="Contract Title"
            rules={[{ required: true, message: 'Please enter a title' }]}
          >
            <Input placeholder="e.g., Salesforce Enterprise Agreement 2024" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="client_vendor_id"
                label={
                  <Space>
                    <span>Vendor</span>
                    {vendorMatches.length > 0 && (
                      <Tag color="blue" style={{ marginLeft: 4 }}>
                        {vendorMatches.length} match{vendorMatches.length > 1 ? 'es' : ''} found
                      </Tag>
                    )}
                  </Space>
                }
              >
                <Select
                  showSearch
                  placeholder="Select vendor"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label || '').toLowerCase().includes(input.toLowerCase())
                  }
                  allowClear
                  options={[
                    // Show AI-matched vendors at top with scores
                    ...(vendorMatches.length > 0 ? [{
                      label: 'AI Suggested Matches',
                      options: vendorMatches.map((match) => ({
                        value: match.client_vendor_id,
                        label: `${match.vendor_name} (${Math.round(match.similarity_score)}% match)`,
                      })),
                    }] : []),
                    // Show all other vendors
                    {
                      label: 'All Vendors',
                      options: vendors
                        .filter(cv => !vendorMatches.find(m => m.client_vendor_id === cv.id))
                        .map((cv) => ({
                          value: cv.id,
                          label: cv.vendor?.name,
                        })),
                    },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="contract_number" label="Contract Number">
                <Input placeholder="e.g., CNT-2024-001" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="contract_type" label="Contract Type">
                <Select placeholder="Select type">
                  <Option value="msa">MSA (Master Service Agreement)</Option>
                  <Option value="sow">SOW (Statement of Work)</Option>
                  <Option value="nda">NDA (Non-Disclosure Agreement)</Option>
                  <Option value="amendment">Amendment</Option>
                  <Option value="renewal">Renewal</Option>
                  <Option value="other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="Status">
                <Select>
                  <Option value="active">Active</Option>
                  <Option value="draft">Draft</Option>
                  <Option value="expired">Expired</Option>
                  <Option value="terminated">Terminated</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="start_date" label="Start Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="end_date" label="End Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="total_value" label="Total Value">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  prefix="$"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="currency" label="Currency">
                <Select>
                  <Option value="USD">USD</Option>
                  <Option value="EUR">EUR</Option>
                  <Option value="GBP">GBP</Option>
                  <Option value="CAD">CAD</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="auto_renew" label="Auto Renew" valuePropName="checked">
                <Select placeholder="Select">
                  <Option value={true}>Yes</Option>
                  <Option value={false}>No</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="renewal_notice_days" label="Notice Days">
                <InputNumber style={{ width: '100%' }} min={0} max={365} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="renewal_term_months" label="Renewal Term (months)">
                <InputNumber style={{ width: '100%' }} min={1} max={60} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notes" label="Notes">
            <TextArea rows={3} placeholder="Additional notes about this contract..." />
          </Form.Item>

          {/* File Upload for editing existing contracts */}
          {editingContract && (
            <Form.Item label="Contract Document">
              <Upload
                beforeUpload={(file) => {
                  setSelectedFile(file);
                  return false;
                }}
                onRemove={() => setSelectedFile(null)}
                fileList={selectedFile ? [selectedFile] : []}
                accept=".pdf,.doc,.docx,.txt"
              >
                <Button icon={<UploadOutlined />}>
                  {editingContract?.file_name ? 'Replace File' : 'Upload Document'}
                </Button>
              </Upload>
              {editingContract?.file_name && !selectedFile && (
                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                  Current file: {editingContract.file_name}
                </Text>
              )}
            </Form.Item>
          )}

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCancel}>Cancel</Button>
              <Button type="primary" htmlType="submit" disabled={extracting}>
                {editingContract ? 'Update' : 'Create Contract'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Contract Detail Drawer */}
      <Drawer
        title={
          <Space>
            {selectedContract?.file_type && getFileIcon(selectedContract.file_type)}
            <span style={{ fontSize: 18, fontWeight: 600 }}>{selectedContract?.title}</span>
            {selectedContract?.contract_type && (
              <Tag color={getTypeColor(selectedContract.contract_type)}>
                {selectedContract.contract_type.toUpperCase()}
              </Tag>
            )}
          </Space>
        }
        placement="right"
        width={520}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        extra={
          <Space>
            {selectedContract?.file_path && (
              <Button icon={<DownloadOutlined />} onClick={() => handleDownload(selectedContract)}>
                Download
              </Button>
            )}
            <Button icon={<EditOutlined />} onClick={() => { setDrawerVisible(false); showEditModal(selectedContract); }}>
              Edit
            </Button>
          </Space>
        }
      >
        {selectedContract && (
          <>
            {/* Status */}
            {(() => {
              const status = getContractStatus(selectedContract);
              return status && (
                <div style={{ marginBottom: 24 }}>
                  <Tag color={status.color} style={{ fontSize: 14, padding: '4px 12px' }}>
                    {status.text}
                    {status.days !== null && (
                      <span> - {Math.abs(status.days)} days {status.days < 0 ? 'ago' : 'remaining'}</span>
                    )}
                  </Tag>
                </div>
              );
            })()}

            {/* Value */}
            {selectedContract.total_value && (
              <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={12}>
                  <Statistic
                    title="Contract Value"
                    value={selectedContract.total_value}
                    prefix="$"
                    valueStyle={{ fontSize: 24 }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Currency"
                    value={selectedContract.currency || 'USD'}
                    valueStyle={{ fontSize: 24 }}
                  />
                </Col>
              </Row>
            )}

            <Divider />

            {/* Contract Details */}
            <Descriptions title="Contract Details" column={1} size="small">
              <Descriptions.Item label="Contract Number">
                {selectedContract.contract_number || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Vendor">
                {(() => {
                  const vendor = vendors.find(v => v.id === selectedContract.client_vendor_id);
                  return vendor?.vendor?.name || '-';
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="Period">
                {selectedContract.start_date
                  ? dayjs(selectedContract.start_date).format('MMM D, YYYY')
                  : '-'}{' '}
                to{' '}
                {selectedContract.end_date
                  ? dayjs(selectedContract.end_date).format('MMM D, YYYY')
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={getStatusColor(selectedContract.status)}>
                  {selectedContract.status?.toUpperCase()}
                </Tag>
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            {/* Renewal Info */}
            <Descriptions title="Renewal Information" column={1} size="small">
              <Descriptions.Item label="Auto Renew">
                {selectedContract.auto_renew ? (
                  <Tag color="green">Yes</Tag>
                ) : (
                  <Tag color="default">No</Tag>
                )}
              </Descriptions.Item>
              {selectedContract.renewal_notice_days && (
                <Descriptions.Item label="Notice Required">
                  {selectedContract.renewal_notice_days} days before expiry
                </Descriptions.Item>
              )}
              {selectedContract.renewal_term_months && (
                <Descriptions.Item label="Renewal Term">
                  {selectedContract.renewal_term_months} months
                </Descriptions.Item>
              )}
            </Descriptions>

            {/* Document Info */}
            {selectedContract.file_name && (
              <>
                <Divider />
                <Descriptions title="Document" column={1} size="small">
                  <Descriptions.Item label="File Name">
                    {selectedContract.file_name}
                  </Descriptions.Item>
                  <Descriptions.Item label="File Type">
                    {selectedContract.file_type?.toUpperCase()}
                  </Descriptions.Item>
                  <Descriptions.Item label="Size">
                    {selectedContract.file_size
                      ? `${(selectedContract.file_size / 1024).toFixed(1)} KB`
                      : '-'}
                  </Descriptions.Item>
                </Descriptions>
              </>
            )}

            {/* Notes */}
            {selectedContract.notes && (
              <>
                <Divider />
                <div>
                  <Title level={5}>Notes</Title>
                  <div style={{
                    background: '#fafafa',
                    padding: 12,
                    borderRadius: 4,
                    whiteSpace: 'pre-wrap',
                  }}>
                    {selectedContract.notes}
                  </div>
                </div>
              </>
            )}

            {/* Timestamps */}
            <Divider />
            <div style={{ color: '#8c8c8c', fontSize: 12 }}>
              <div>Created: {dayjs(selectedContract.created_at).format('MMM D, YYYY')}</div>
              <div>Last updated: {dayjs(selectedContract.updated_at).format('MMM D, YYYY h:mm A')}</div>
            </div>
          </>
        )}
      </Drawer>
    </div>
  );
};

export default Contracts;
